import React, { useCallback, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { TokenUsage, TokenStepDetail } from './useAgentSession';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    tokenUsage: TokenUsage;
    debugMode: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function formatNumber(count: number): string {
    if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
    if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
    return String(count);
}

function formatTime(timestamp: string): string {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// ─── Bar Chart ───────────────────────────────────────────────────────────

const BAR_COLORS = {
    noCacheInput: 'bg-blue-500',
    cacheRead: 'bg-emerald-500',
    cacheWrite: 'bg-amber-500',
    output: 'bg-violet-500',
    reasoning: 'bg-rose-500',
} as const;

function StepBar({ step, maxTokens }: { step: TokenStepDetail; maxTokens: number }) {
    const total = step.noCacheInputTokens + step.cacheReadTokens + step.cacheWriteTokens + step.outputTokens + step.reasoningTokens;
    const widthPercent = maxTokens > 0 ? (total / maxTokens) * 100 : 0;

    const segments = [
        { key: 'noCacheInput', label: 'New input', value: step.noCacheInputTokens, color: BAR_COLORS.noCacheInput },
        { key: 'cacheRead', label: 'Cached context', value: step.cacheReadTokens, color: BAR_COLORS.cacheRead },
        { key: 'cacheWrite', label: 'Newly cached', value: step.cacheWriteTokens, color: BAR_COLORS.cacheWrite },
        { key: 'output', label: 'Output', value: step.outputTokens, color: BAR_COLORS.output },
        { key: 'reasoning', label: 'Reasoning', value: step.reasoningTokens, color: BAR_COLORS.reasoning },
    ].filter(segment => segment.value > 0);

    return (
        <div className="flex items-center gap-2 group">
            <span className="text-[10px] font-mono text-neutral-500 dark:text-neutral-400 w-6 text-right flex-shrink-0">
                {step.stepNumber}
            </span>
            <div className="flex-1 h-5 bg-neutral-100 dark:bg-neutral-800 rounded overflow-hidden">
                <div className="h-full flex" style={{ width: `${Math.max(widthPercent, 1)}%` }}>
                    {segments.map(segment => {
                        const segmentPercent = total > 0 ? (segment.value / total) * 100 : 0;
                        return (
                            <div
                                key={segment.key}
                                className={cn(segment.color, 'h-full transition-all')}
                                style={{ width: `${segmentPercent}%` }}
                                title={`${segment.label}: ${formatNumber(segment.value)}`}
                            />
                        );
                    })}
                </div>
            </div>
            <span className="text-[10px] font-mono text-neutral-400 dark:text-neutral-500 w-12 text-right flex-shrink-0">
                {formatNumber(total)}
            </span>
        </div>
    );
}

// ─── Legend ───────────────────────────────────────────────────────────────

function Legend() {
    const items = [
        { label: 'New input', color: BAR_COLORS.noCacheInput },
        { label: 'Cached context', color: BAR_COLORS.cacheRead },
        { label: 'Newly cached', color: BAR_COLORS.cacheWrite },
        { label: 'Output', color: BAR_COLORS.output },
        { label: 'Reasoning', color: BAR_COLORS.reasoning },
    ];

    return (
        <div className="flex flex-wrap gap-3">
            {items.map(item => (
                <div key={item.label} className="flex items-center gap-1.5">
                    <div className={cn('w-2.5 h-2.5 rounded-sm', item.color)} />
                    <span className="text-[10px] text-neutral-500 dark:text-neutral-400">{item.label}</span>
                </div>
            ))}
        </div>
    );
}

// ─── Summary Header ──────────────────────────────────────────────────────

function SummaryHeader({ tokenUsage }: { tokenUsage: TokenUsage }) {
    const { steps } = tokenUsage;
    const totalTokens = tokenUsage.totalInput + tokenUsage.totalOutput;
    const totalCacheRead = steps.reduce((sum, step) => sum + step.cacheReadTokens, 0);
    const cacheHitRate = tokenUsage.totalInput > 0 ? (totalCacheRead / tokenUsage.totalInput) * 100 : 0;

    // Estimate system prompt size from the first step's cached content
    // On step 1 the only cached content is the system prompt (tagged with cacheControl ephemeral)
    const firstStep = steps[0];
    const systemPromptEstimate = firstStep ? Math.max(firstStep.cacheReadTokens, firstStep.cacheWriteTokens) : 0;

    // Current context window = last step's total input tokens
    const lastStep = steps[steps.length - 1];
    const currentContext = lastStep?.inputTokens ?? 0;

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-5 gap-4">
                <SummaryStat label="Total billed" value={formatNumber(totalTokens)} />
                <SummaryStat label="Total input" value={formatNumber(tokenUsage.totalInput)} />
                <SummaryStat label="Total output" value={formatNumber(tokenUsage.totalOutput)} />
                <SummaryStat label="Cache hit rate" value={steps.length > 0 ? `${cacheHitRate.toFixed(1)}%` : '--'} />
                <SummaryStat label="Steps" value={steps.length > 0 ? String(steps.length) : '--'} />
            </div>
            {steps.length > 0 && (
                <div className="flex gap-4 text-[10px] text-neutral-500 dark:text-neutral-400">
                    <span>System prompt: ~{formatNumber(systemPromptEstimate)} tokens</span>
                    <span className="text-neutral-300 dark:text-neutral-600">|</span>
                    <span>Current context: {formatNumber(currentContext)} tokens</span>
                    <span className="text-neutral-300 dark:text-neutral-600">|</span>
                    <span>Conversation growth: +{formatNumber(currentContext - systemPromptEstimate)} tokens</span>
                </div>
            )}
        </div>
    );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex flex-col gap-0.5">
            <span className="text-[10px] text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{label}</span>
            <span className="text-[18px] font-semibold text-neutral-900 dark:text-neutral-100 font-mono">{value}</span>
        </div>
    );
}

// ─── Detail Table ────────────────────────────────────────────────────────

function DetailTable({ steps }: { steps: TokenStepDetail[] }) {
    return (
        <div className="overflow-auto max-h-[35vh] rounded-lg border border-neutral-200 dark:border-neutral-700">
            <table className="w-full text-[11px] font-mono">
                <thead className="sticky top-0 bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
                    <tr>
                        <th className="px-2 py-1.5 text-left font-medium text-neutral-500 dark:text-neutral-400">#</th>
                        <th className="px-2 py-1.5 text-left font-medium text-neutral-500 dark:text-neutral-400">Time</th>
                        <th className="px-2 py-1.5 text-left font-medium text-neutral-500 dark:text-neutral-400">Tools</th>
                        <th className="px-2 py-1.5 text-right font-medium text-neutral-500 dark:text-neutral-400">Context</th>
                        <th className="px-2 py-1.5 text-right font-medium text-neutral-500 dark:text-neutral-400">New In</th>
                        <th className="px-2 py-1.5 text-right font-medium text-neutral-500 dark:text-neutral-400">Cached</th>
                        <th className="px-2 py-1.5 text-right font-medium text-neutral-500 dark:text-neutral-400">New Cache</th>
                        <th className="px-2 py-1.5 text-right font-medium text-neutral-500 dark:text-neutral-400">Output</th>
                        <th className="px-2 py-1.5 text-right font-medium text-neutral-500 dark:text-neutral-400">Reasoning</th>
                        <th className="px-2 py-1.5 text-right font-medium text-neutral-500 dark:text-neutral-400">Tot. In</th>
                        <th className="px-2 py-1.5 text-right font-medium text-neutral-500 dark:text-neutral-400">Tot. Out</th>
                        <th className="px-2 py-1.5 text-left font-medium text-neutral-500 dark:text-neutral-400">Finish</th>
                    </tr>
                </thead>
                <tbody>
                    {steps.map((step, index) => (
                        <tr
                            key={step.stepNumber}
                            className={cn(
                                index % 2 === 0 ? 'bg-white dark:bg-neutral-900' : 'bg-neutral-50/50 dark:bg-neutral-800/50',
                                'hover:bg-blue-50/50 dark:hover:bg-blue-950/20 transition-colors',
                            )}
                        >
                            <td className="px-2 py-1 text-neutral-600 dark:text-neutral-300">{step.stepNumber}</td>
                            <td className="px-2 py-1 text-neutral-500 dark:text-neutral-400">{formatTime(step.timestamp)}</td>
                            <td className="px-2 py-1 text-neutral-600 dark:text-neutral-300 max-w-[120px] truncate">
                                {step.toolNames.length > 0 ? step.toolNames.join(', ') : '--'}
                            </td>
                            <td className="px-2 py-1 text-right font-medium text-neutral-700 dark:text-neutral-200">
                                {formatNumber(step.inputTokens)}
                            </td>
                            <td className="px-2 py-1 text-right text-blue-600 dark:text-blue-400">
                                {formatNumber(step.noCacheInputTokens)}
                            </td>
                            <td className="px-2 py-1 text-right text-emerald-600 dark:text-emerald-400">
                                {formatNumber(step.cacheReadTokens)}
                            </td>
                            <td className="px-2 py-1 text-right text-amber-600 dark:text-amber-400">
                                {formatNumber(step.cacheWriteTokens)}
                            </td>
                            <td className="px-2 py-1 text-right text-violet-600 dark:text-violet-400">{formatNumber(step.outputTokens)}</td>
                            <td className="px-2 py-1 text-right text-rose-600 dark:text-rose-400">{formatNumber(step.reasoningTokens)}</td>
                            <td className="px-2 py-1 text-right text-neutral-500 dark:text-neutral-400">
                                {formatNumber(step.cumulativeInputTokens)}
                            </td>
                            <td className="px-2 py-1 text-right text-neutral-500 dark:text-neutral-400">
                                {formatNumber(step.cumulativeOutputTokens)}
                            </td>
                            <td className="px-2 py-1 text-neutral-500 dark:text-neutral-400">{step.finishReason}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ─── Main Component ──────────────────────────────────────────────────────

export const TokenUsageDialog: React.FC<Props> = ({ open, onOpenChange, tokenUsage, debugMode }) => {
    const [copied, setCopied] = useState(false);
    const { steps } = tokenUsage;

    const maxTokens =
        steps.length > 0
            ? Math.max(
                  ...steps.map(
                      step =>
                          step.noCacheInputTokens + step.cacheReadTokens + step.cacheWriteTokens + step.outputTokens + step.reasoningTokens,
                  ),
              )
            : 0;

    const copyToClipboard = useCallback(() => {
        navigator.clipboard.writeText(JSON.stringify(steps, null, 2)).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    }, [steps]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[95vw] h-[90vh] flex flex-col p-0 gap-0">
                <DialogHeader className="px-6 pt-5 pb-4 border-b border-neutral-200 dark:border-neutral-700 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div>
                            <DialogTitle className="text-[16px]">Token Usage</DialogTitle>
                            <DialogDescription className="text-[12px] mt-1">
                                {steps.length > 0 ? `${steps.length} step${steps.length !== 1 ? 's' : ''} recorded` : 'Per-step breakdown'}
                            </DialogDescription>
                        </div>
                        {steps.length > 0 && (
                            <button
                                onClick={copyToClipboard}
                                className={cn(
                                    'px-3 py-1.5 rounded-md text-[11px] font-medium mr-8',
                                    'border border-neutral-200 dark:border-neutral-700',
                                    'text-neutral-600 dark:text-neutral-300',
                                    'hover:bg-neutral-100 dark:hover:bg-neutral-800',
                                    'transition-colors',
                                )}
                            >
                                {copied ? 'Copied!' : 'Copy JSON'}
                            </button>
                        )}
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6 min-h-0">
                    {/* Summary */}
                    <SummaryHeader tokenUsage={tokenUsage} />

                    {steps.length > 0 ? (
                        <>
                            {/* Bar Chart */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-[12px] font-medium text-neutral-700 dark:text-neutral-300">
                                        Per-step token breakdown
                                    </h3>
                                    <Legend />
                                </div>
                                <div className="space-y-1">
                                    {steps.map(step => (
                                        <StepBar key={step.stepNumber} step={step} maxTokens={maxTokens} />
                                    ))}
                                </div>
                            </div>

                            {/* Detail Table */}
                            <div className="space-y-2">
                                <h3 className="text-[12px] font-medium text-neutral-700 dark:text-neutral-300">Step details</h3>
                                <DetailTable steps={steps} />
                            </div>
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <div className="text-[13px] text-neutral-500 dark:text-neutral-400">
                                {debugMode
                                    ? 'No steps recorded yet. Send a message to start collecting data.'
                                    : 'Enable debug mode to collect per-step token data.'}
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};
